<template>
	<step
		:name="optionGroup.name + '-' + optionGroup.id" :id="optionGroup.id"
		v-if="!hasSingleOption && !isGroupLockedFromEditor && !isGroupHidden"
		:data-id="optionGroup.id" :title="optionGroup.title">
		<template slot="title">
			<div class="grid">
				<template v-if="getThisSelectedOption[optionGroup.id]">
					<div class="h5 configurator-step__title cursor-pointer" @click="setStepActive(optionGroup.id)">
						<span
							class="configurator-step__count  configurator-step__count--valid"
							v-if="getThisSelectedOption[optionGroup.id].name" />
						<span v-else class="configurator-step__count">{{ getStepInt }}</span>
						{{ optionGroup.name }}
						<span
							v-if="optionGroup.isOnlyForTesting"
							class="only-for-testing--tag"
							title="Deze stap is alleen zichtbaar voor jou omdat je bent ingelogd met een intern testaccount. Klanten zien deze stap niet.">
							OnlyForTesting
						</span>
						<span class="configurator-step__semi-colon configurator-step__not-active-mobile">:</span>
						<span class="configurator-step__item configurator-step__not-active-mobile">
							{{ getThisSelectedOption[optionGroup.id].name }}
						</span>
					</div>
				</template>

				<template v-else>
					<div class="h5 configurator-step__title">
						<span class="configurator-step__count">{{ getStepInt }}</span>
						{{ optionGroup.title }}
						<span
							v-if="optionGroup.isOnlyForTesting"
							class="only-for-testing--tag"
							title="Deze stap is alleen zichtbaar voor jou omdat je bent ingelogd met een intern testaccount. Klanten zien deze stap niet.">
							OnlyForTesting
						</span>
					</div>
				</template>
			</div>
		</template>

		<description v-if="hasDescription" />

		<div class="configurator-options grid grid--gutters" :class="setView.className">

			<div class="cell cell--fit">
				<div class="configurator-options grid grid--gutters-s grid--1-4 grid--l-1-3 grid--ms-1-2">

					<option-item
						v-for="(option, index) in visibleProductOptions" :key="index"
						:option="option"
						@setSelectedOptionImage="setSelectedChildOptionImage" :group="groupData" />

					<div class="cell configurator-options__wrapper" v-if="enableShowMore">
						<div class="configurator-options__item cursor-pointer" @click="toggleShowAllOptions">
							<span class="configurator-options__additional-icon">{{ showAllOptions ? "-" : "+" }}
							</span>
							<div class="h4 configurator-block__label configurator-block__label--inline configurator-options__additional-text">
								{{ showAllOptions ? "Minder" : "Alle" }} opties weergeven
							</div>
						</div>
					</div>

				</div>

				<div
					v-if="showPrintableEndPapersAlert"
					class="cell cell--full alertbox alertbox--notification alertbox--inline shoppingcart-proof-message mt-s">
					<svg class="alertbox__icon alertbox__icon--notification">
						<use xlink:href="#alert--info" />
					</svg>
					<p class="alertbox__text">
						Als u voor bedrukte schutbladen kiest moet u het product "<a
						href="/boeken/bedrukte-schutbladen/" target="_blank">Bedrukte schutbladen</a>"
						los in uw winkelwagen plaatsen. De bestanden hiervoor dient u los van het boek te uploaden.
					</p>
				</div>

			</div>
		</div>
	</step>
	<div
		v-else-if="isGroupLockedFromEditor && shouldShowLockedStep && getThisSelectedOption[optionGroup.id]" id="papiersoort"
		class="configurator-step configurator-step--validstate configurator-step--locked">
		<div class="configurator-step__identifier configurator-step__identifier--locked">
			<div class="configurator-step__line" />
			<span class="configurator-step__icon configurator-step__icon--locked">
				<div class="offset-indicator__icon tool-tip__arrow-hook" v-html="getSvg.locked" />
			</span>
		</div>
		<div class="configurator-step__content">
			<div class="h5 configurator-step__title">
				<strong>{{ optionGroup.name }}</strong>
				: {{ getThisSelectedOption[optionGroup.id].name }}
			</div>
		</div>
	</div>
</template>

<script>
import step from "@/components/step/step";
import optionItem from "@/components/option/option-item";
import description from "@/components/element/description";

export default {
	name: "OptionGroups",
	components: { step, optionItem, description },
	props: {
		optionGroup: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			optionalChildImage: null,
			showAllOptions: false,
			hasClickedTitle: false
		};
	},
	computed: {
		setView () {
			const viewObject = {
				0: {
					className: "grid--center-vertical grid--center-horizontal grid--ms-full",
					rounded: false,
					image: true
				},
				1: {
					className: "grid--center-vertical grid--center-horizontal grid--ms-full",
					rounded: false,
					image: false
				},
				2: {
					className: "grid--center-vertical grid--center-horizontal grid--ms-full",
					rounded: false,
					image: true
				},
				3: {
					className: "grid--center-vertical grid--center-horizontal grid--ms-full",
					rounded: true,
					image: true
				},
				4: {
					className: "grid--center-vertical grid--center-horizontal grid--ms-full",
					rounded: true,
					image: true
				}
			};

			return viewObject[this.optionGroup.optionCategory];
		},
		getCurrentStep () {
			return this.$store.getters.getCurrentStep;
		},
		unavailableOptions () {
			return this.$store.getters.getAllUnavailableOptions;
		},
		getAllSteps () {
			return this.$store.getters.getAllSteps;
		},
		getStepInt () {
			const step = this.getAllSteps.find(s => s.title === this.optionGroup.title);
			return this.getAllSteps.indexOf(step) + 1;
		},
		groupActive () {
			let indexNum = this.getCurrentStep.index;
			if (indexNum >= Object.keys(this.getAllSteps).length) {
				indexNum = 0;
			}

			const currentGroupId = this.getAllSteps[indexNum].groupId;
			return currentGroupId === this.optionGroup.id;
		},
		groupData () {
			return {
				id: this.optionGroup.id,
				name: this.optionGroup.name,
				supportsCombinedPrint: this.optionGroup.supportsCombinedPrint,
				type: this.optionGroup.optionCategory,
				rounded: this.setView.rounded,
				image: this.setView.image,
				chunkType: this.optionGroup.chunkType,
				totalGroupOptions: this.optionGroup.productOptions.length,
				groupActive: this.groupActive,
				visibleOptionIds: this.visibleProductOptionsList
			};
		},
		gridClass () {
			return this.isOffTheShelf
				? "grid--gutters grid--1-5 grid--ms-1-3 grid--s-1-2"
				: "grid--1-2 grid--gutters-inline grid--ms-full";
		},
		dictionary () {
			return this.$store.getters.getDictionary;
		},
		isBookProduct () {
			return this.$store.getters.isBookProduct;
		},
		hasDescription () {
			return (
				this.dictionary.optionCategory[this.optionGroup.optionCategory] === "print"
				&& this.dictionary.chunkType[this.optionGroup.chunkType] === "content"
				&& this.isBookProduct
			);
		},
		checkData () {
			return this.$store.getters.checkData;
		},
		getThisSelectedOption () {
			return this.$store.getters.getGroupIndexedSelectedOptions;
		},
		uploadsComplete () {
			return this.$store.getters.uploadsAreComplete;
		},
		// very ugly but necessary check for 'bedrukte schutbladen' product..
		showPrintableEndPapersAlert () {
			return this.getThisSelectedOption[this.optionGroup.id]
				? [411, 412].includes(this.getThisSelectedOption[this.optionGroup.id].baseOptionId)
				: false;
		},
		hasSingleOption () {
			return this.optionGroup.productOptions.length === 1;
		},
		visibleProductOptionsList () {
			// Fall back to all options when no option is marked directly visible,
			// otherwise a group with every option flagged CONFIGURATOR_IS_HIDDEN_BY_DEFAULT
			// renders empty and the step can't be completed.
			if (!this.optionGroup.directlyVisibleOptions
				|| this.optionGroup.directlyVisibleOptions.length === 0) {
				return this.optionGroup.productOptions.filter(
					o => !this.unavailableOptions.includes(o.id)
				);
			}

			const thisSelectedOption = () => {
				const option = this.getThisSelectedOption[this.optionGroup.id];
				return option ? option.id : null;
			};

			const visibleOptionIds = [].concat(
				this.optionGroup.directlyVisibleOptions,
				thisSelectedOption()
			);

			return this.optionGroup.productOptions.filter(o =>
				visibleOptionIds.includes(o.id)
				&& !this.unavailableOptions.includes(o.id)
			);
		},
		visibleProductOptions () {
			const selectedOption = this.getThisSelectedOption[this.optionGroup.id] || null;

			if (!this.showAllOptions) {
				if (this.optionGroup.directlyVisibleOptions
					&& selectedOption
					&& !this.optionGroup.directlyVisibleOptions.includes(selectedOption.id)
					&& this.optionGroup.directlyVisibleOptions.length > 1
				) {
					return [...this.visibleProductOptionsList.filter(o => o.id !== selectedOption.id), selectedOption];
				}

				return this.visibleProductOptionsList;
			}

			let visibleOptions = [...this.visibleProductOptionsList];

			if (selectedOption && !this.optionGroup.directlyVisibleOptions.includes(selectedOption.id)) {
				visibleOptions = [...this.visibleProductOptionsList.filter(o => o.id !== selectedOption.id)];
			}

			// Filter out unavailable/excluded options even when showing all
			const availableProductOptions = this.optionGroup.productOptions.filter(
				o => !this.unavailableOptions.includes(o.id)
			);
			return Array.from(new Set([...visibleOptions, ...availableProductOptions]));
		},
		enableShowMore () {
			return (
				this.optionGroup.productOptions.length > this.optionGroup.directlyVisibleOptions.length
				&& this.visibleProductOptions.length > 1
			);
		},
		isOffTheShelf () {
			return this.$store.getters.isOffTheShelf;
		},
		getConfigurationType () {
			return this.$store.getters.getConfiguratorType;
		},
		isGroupLockedFromEditor () {
			return this.getConfigurationType.isConfiguration && this.$store.getters.getConfigurationSettings.productGroupIdsForcedByEditor.includes(this.optionGroup.id);
		},
		isGroupHidden () {
			// Tagged Hidden: never shown to the customer, unlike "locked from editor" this applies
			// regardless of whether we're editing an existing configuration.
			return !!this.optionGroup.isHidden;
		},
		getSvg () {
			return this.$store.getters.getSvg;
		},
		shouldShowLockedStep () {
			return !["configurator-overview", "price-table", "uploader"].includes(this.getCurrentStep.data.name);
		}
	},
	mounted () {
		if (this.hasSingleOption) {
			const option = this.optionGroup.productOptions[0];
			option.parentGroupId = this.optionGroup.id;
			this.$store.commit("SET_SELECTED_OPTION", option);
		}
	},
	created () {
		this.setOptionImageInitial();
	},
	methods: {
		toggleShowAllOptions () {
			this.showAllOptions = !this.showAllOptions;
		},
		setSelectedChildOptionImage (value) {
			this.optionalChildImage = value;
		},
		setOptionImageInitial () {
			let imagePath;
			const bestChoice = this.optionGroup.productOptions.find(
				i => i.isBestChoice
			);
			if (bestChoice) {
				imagePath = bestChoice.iconPath;
			} else {
				imagePath = this.optionGroup.productOptions[0].iconPath;
			}
			this.optionalChildImage = imagePath;
		},
		stepExists (stepToCheck) {
			return this.$store.getters.getStepExists(stepToCheck);
		},
		setStepActive (step) {
			if (this.stepExists(step).bool) {
				this.hasClickedTitle = true;
				this.$store.commit("SET_STEP_ACTIVE", step);
			}
		},
		nextStep () {
			this.$store.commit("CONFIGURATOR_STEP_NEXT");
		},
		checkVisibleOptions () {
			const filteredOptions = this.optionGroup.productOptions.filter(
				option => !this.unavailableOptions.includes(option.id)
			);

			if (filteredOptions.length === 1 && !this.hasClickedTitle && this.optionGroup.title === this.getCurrentStep.data.title) {
				this.nextStep();
			}
		}
	},
	watch: {
		getThisSelectedOption: function (a, b) {
			if (this.optionGroup) {
				if (!a[this.optionGroup.id]) {
					const thisGroup = this.getAllSteps.find(
						a => a.groupId === this.optionGroup.id
					);

					if (!thisGroup) {
						return;
					}

					const groupIndex = this.getAllSteps.indexOf(thisGroup);
					this.$store.commit("SET_STEP_INVALID", groupIndex);
				}
			}
		},
		groupActive: function (a, b) {
			this.showAllOptions = false;
		},
		"getCurrentStep.index": function (newOptions, oldOptions) {
			this.checkVisibleOptions();
		}
	}
};
</script>

<style>
.configurator-options__additional-icon {
	background: #f0f0f0;
	color: #ccc;
	display: inline-flex;
	justify-content: center;
	align-items: center;
	height: 25px;
	border-radius: 50px;
	line-height: 0;
	font-size: 20px;
	width: 25px;
}

.configurator-options__additional-text {
	color: #999;
}
</style>



// WEBPACK FOOTER //
// src/components/option/option-groups.vue